import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Add16Regular, Checkmark16Regular, Dismiss16Regular, PulseRegular, Warning16Filled } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PolicyRule, PolicyStatus } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { NoAccess } from '../components/NoAccess';
import { PageAside } from '../components/PageAside';
import { PageHeader } from '../components/PageHeader';
import { formatDayTime } from '../lib/format';
import { useShell } from '../lib/shell';
import { errorText } from '../lib/useRelay';
import { vt } from '../theme';
import { DiffDialog } from './firewall/DiffDialog';
import { blankRule, moveRule, ruleHits, tagVocabulary } from './firewall/model';
import { CompiledPanel, HistoryTable, TagsPanel } from './firewall/panels';
import { RuleEditor } from './firewall/RuleEditor';
import { RuleTable } from './firewall/RuleTable';
import { usePolicy, type UsePolicy } from './firewall/usePolicy';

/** The mockup opens the editor on rule 1; only where there is room for the second pane. */
export const EDITOR_OPEN_MIN_WIDTH = 1400;

type TabId = 'rules' | 'tags' | 'history';
const TABS: { id: TabId; label: string }[] = [
  { id: 'rules', label: 'Rules' },
  { id: 'tags', label: 'Tags' },
  { id: 'history', label: 'History' },
];

const useStyles = makeStyles({
  tabs: { display: 'flex', columnGap: '4px' },
  tab: {
    height: '28px',
    paddingInline: '12px',
    border: 'none',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'transparent',
    color: vt.fg3,
    fontFamily: 'inherit',
    fontSize: '12.5px',
    cursor: 'pointer',
    ':hover': { color: vt.fg2 },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  tabSelected: { backgroundColor: vt.tintNeutral, color: vt.fg1, fontWeight: tokens.fontWeightSemibold },
  banner: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    minHeight: '40px',
    paddingInline: '14px',
    borderRadius: '8px',
    border: `1px solid ${vt.tintOrange}`,
    backgroundColor: vt.tintOrange,
    fontSize: '12.5px',
    color: vt.fg2,
  },
  bannerIcon: { color: vt.orange, flexShrink: 0 },
  bannerTitle: { fontWeight: tokens.fontWeightSemibold, color: vt.fg1, whiteSpace: 'nowrap' },
  bannerBody: { flexGrow: 1, minWidth: 0, fontSize: '12px', paddingBlock: '6px' },
  link: { fontWeight: tokens.fontWeightSemibold, color: vt.brand, flexShrink: 0, minWidth: 0, paddingInline: '4px' },
  clean: { fontSize: '12px', color: vt.fg3 },
  tableTools: { display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' },
  lower: { display: 'grid', gridTemplateColumns: 'minmax(0, 452fr) minmax(0, 282fr)', columnGap: '32px', alignItems: 'start' },
});

function DraftBanner({ policy, onReview }: { policy: UsePolicy; onReview: () => void }) {
  const s = useStyles();
  const { diff, draft, active } = policy;
  if (!diff || !draft || !active) return null;
  const n = diff.changeCount;
  if (n === 0) {
    return (
      <div className={s.clean} role="status">
        No unpublished changes · the relay enforces v{active.version}
        {active.publishedAt && `, published ${formatDayTime(active.publishedAt)} by ${active.publishedBy ?? 'unknown'}`}
      </div>
    );
  }
  const enforcing =
    active.version === 0
      ? 'nothing is published yet, the relay runs the open policy'
      : `the relay is still enforcing v${active.version}, published ${formatDayTime(active.publishedAt)} by ${active.publishedBy ?? 'unknown'}`;
  return (
    <div className={s.banner} role="status" aria-label="Draft">
      <Warning16Filled className={s.bannerIcon} />
      <span className={s.bannerTitle}>
        {n} unpublished change{n === 1 ? '' : 's'}
      </span>
      <span className={s.bannerBody}>
        draft r{draft.revision} · {enforcing}
      </span>
      <Button appearance="transparent" size="small" className={s.link} onClick={onReview}>
        Review diff
      </Button>
    </div>
  );
}

function PublishDialog({
  open,
  policy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  policy: UsePolicy;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const n = policy.diff?.changeCount ?? 0;
  const next = (policy.active?.version ?? 0) + 1;
  return (
    <Dialog open={open} onOpenChange={(_e, data) => !data.open && onCancel()}>
      <DialogSurface aria-label="Publish the draft">
        <DialogBody>
          <DialogTitle>Publish v{next}?</DialogTitle>
          <DialogContent>
            Draft r{policy.draft?.revision} with {n} change{n === 1 ? '' : 's'} becomes v{next}. The relay applies it as one
            atomic nftables transaction on its next policy tick (within a minute while it is running).
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onConfirm} disabled={policy.busy !== null}>
              Publish
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface Editing {
  /** 0-based position; for a new rule, where it will be appended. */
  index: number;
  isNew: boolean;
}

/**
 * GET /policy/status for the Hits 24 h and Last match columns: on open and after a publish.
 * Only counters, so a failure leaves the cells as dashes and says why once.
 */
function usePolicyStatus() {
  const { api } = useAuth();
  const [status, setStatus] = useState<PolicyStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setStatus(await api.policyStatus());
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, [api]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { status, error, reload };
}

function Policy() {
  const s = useStyles();
  const policy = usePolicy(true);
  const counters = usePolicyStatus();
  const { devices } = useShell();
  const { draft, diff, history, active, compiled, busy } = policy;
  const [tab, setTab] = useState<TabId>('rules');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const compiledRef = useRef<HTMLElement>(null);
  const opened = useRef(false);

  const rules = useMemo(() => draft?.rules ?? [], [draft]);
  const list = useMemo(() => devices ?? [], [devices]);
  const vocabulary = useMemo(() => tagVocabulary(list, rules), [list, rules]);
  const changes = diff?.changeCount ?? 0;

  // As in the mockup: with room for the inspector, it opens on rule 1.
  useEffect(() => {
    if (opened.current || !draft) return;
    opened.current = true;
    if (draft.rules.length > 0 && window.innerWidth >= EDITOR_OPEN_MIN_WIDTH) setEditing({ index: 0, isNew: false });
  }, [draft]);

  // A reload (412, another tab) can shorten the list under the editor.
  const editingRule: PolicyRule | null = editing
    ? editing.isNew
      ? blankRule()
      : (rules[editing.index] ?? null)
    : null;

  const save = async (rule: PolicyRule) => {
    if (!editing) return;
    const next = [...rules];
    if (editing.isNew) next.push(rule);
    else next[editing.index] = rule;
    if ((await policy.saveRules(next)) && editing.isNew) setEditing({ index: next.length - 1, isNew: false });
  };
  const remove = async () => {
    if (!editing || editing.isNew) return;
    const at = editing.index;
    if (await policy.saveRules(rules.filter((_, i) => i !== at))) setEditing(null);
  };
  const move = async (from: number, to: number) => {
    if (await policy.saveRules(moveRule(rules, from, to))) {
      if (editing && !editing.isNew && editing.index === from) setEditing({ index: to, isNew: false });
    }
  };
  const runDryRun = async () => {
    setTab('rules');
    await policy.dryRun();
    compiledRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  };
  const publish = async () => {
    setConfirming(false);
    if (await policy.publish()) void counters.reload();
  };

  return (
    <>
      <PageHeader
        title="Firewall"
        actions={
          <>
            <Button
              appearance="outline"
              shape="circular"
              size="small"
              icon={<PulseRegular fontSize={16} />}
              disabled={!draft || busy !== null}
              onClick={() => void runDryRun()}
            >
              Dry run
            </Button>
            <Button
              appearance="primary"
              shape="circular"
              size="small"
              icon={<Checkmark16Regular />}
              disabled={!draft || changes === 0 || busy !== null}
              onClick={() => setConfirming(true)}
            >
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        }
      >
        <div className={s.tabs} role="tablist" aria-label="Firewall views">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={mergeClasses(s.tab, tab === t.id && s.tabSelected)}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </PageHeader>

      {policy.notice && (
        <MessageBar intent="warning">
          <MessageBarBody>{policy.notice}</MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button appearance="transparent" aria-label="Dismiss" icon={<Dismiss16Regular />} onClick={policy.dismissNotice} />
            }
          />
        </MessageBar>
      )}
      {policy.error && (
        <MessageBar intent="error">
          <MessageBarBody>{policy.error}</MessageBarBody>
        </MessageBar>
      )}
      {counters.error && (
        <MessageBar intent="warning">
          <MessageBarBody>Hit counters unavailable: {counters.error}</MessageBarBody>
        </MessageBar>
      )}
      {policy.loading && !draft && <Spinner label="Loading the policy" />}

      {draft && tab !== 'history' && <DraftBanner policy={policy} onReview={() => setReviewing(true)} />}

      {draft && tab === 'rules' && (
        <>
          <RuleTable
            rules={rules}
            diff={diff}
            selected={editing && !editing.isNew ? editing.index : null}
            onSelect={(index) => setEditing({ index, isNew: false })}
            onMove={(from, to) => void move(from, to)}
            disabled={busy !== null}
            status={counters.status}
          />
          <div className={s.tableTools}>
            <Button
              appearance="subtle"
              shape="circular"
              size="small"
              icon={<Add16Regular />}
              disabled={busy !== null}
              onClick={() => setEditing({ index: rules.length, isNew: true })}
            >
              Add rule
            </Button>
          </div>
          <div className={s.lower}>
            <CompiledPanel
              ref={compiledRef}
              compiled={compiled}
              focusId={editing && !editing.isNew ? (rules[editing.index]?.id ?? null) : null}
            />
            <TagsPanel vocabulary={vocabulary} />
          </div>
        </>
      )}
      {draft && tab === 'tags' && <TagsPanel vocabulary={vocabulary} />}
      {tab === 'history' && history && <HistoryTable versions={history} activeVersion={active?.version ?? null} />}

      {editing && editingRule && tab === 'rules' && (
        <PageAside label="Rule editor">
          <RuleEditor
            key={editing.isNew ? 'new' : editing.index}
            rule={editingRule}
            index={editing.index}
            count={rules.length}
            isNew={editing.isNew}
            allRules={rules}
            devices={list}
            busy={busy !== null}
            hits={ruleHits(counters.status, editingRule.id)}
            onSave={(r) => void save(r)}
            onDelete={() => void remove()}
            onMove={(to) => void move(editing.index, to)}
            onClose={() => setEditing(null)}
          />
        </PageAside>
      )}

      <DiffDialog diff={diff} open={reviewing} onClose={() => setReviewing(false)} />
      <PublishDialog open={confirming} policy={policy} onCancel={() => setConfirming(false)} onConfirm={() => void publish()} />
    </>
  );
}

/** The policy editor (mockup 05), admin only: rules, draft and publish, compiled output, tags, history. */
export function Firewall() {
  const { me, loading, error, isAdmin } = useMe();
  return (
    <>
      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !isAdmin && (
        <>
          <PageHeader title="Firewall" />
          <NoAccess />
        </>
      )}
      {me && isAdmin && <Policy />}
    </>
  );
}
